import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";

import { cn } from "@/utils/cn";

import { api, unwrap } from "@/api/client";
import { ME_KEY, useMe } from "@/auth";
import Icon, { type IconName } from "@/components/icon";
import IconBadge from "@/components/icon-badge";
import Text from "@/components/text";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { setTheme, type ThemeChoice, useTheme } from "@/lib/theme";

function NavItem({ to, icon, label, end }: { to: string; icon: IconName; label: string; end?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "inline-flex h-9 items-center gap-2 rounded-lg px-3.5 text-sm font-medium transition-colors",
          isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon name={icon} size={18} variant={isActive ? "Bold" : "Linear"} />
          {label}
        </>
      )}
    </NavLink>
  );
}

export function Layout() {
  const me = useMe().data;
  const theme = useTheme();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const logout = useMutation({
    mutationFn: () => unwrap(api.POST("/api/auth/logout")),
    onSettled: () => {
      queryClient.clear();
      queryClient.setQueryData(ME_KEY, null);
      navigate("/login", { replace: true });
    },
  });

  if (!me) return null;

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="sticky top-0 z-30 border-b bg-card">
        <div className="mx-auto flex h-16 w-full max-w-[1600px] items-center gap-6 px-6">
          <Link to="/runs" className="flex shrink-0 items-center gap-2.5">
            <IconBadge tone="blue" className="h-8">
              <Icon name="accounts" variant="Bold" size={18} />
            </IconBadge>
            <Text weight="semibold" size="subtitle">
              Bulk Register
            </Text>
          </Link>

          <nav className="no-scrollbar flex flex-1 items-center gap-1 overflow-x-auto">
            <NavItem to="/runs" icon="runs" label="Runs" end />
            <NavItem to="/runs/new" icon="add" label="New run" />
            {me.is_admin && <NavItem to="/users" icon="people" label="Users" />}
          </nav>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex shrink-0 items-center gap-2 rounded-lg py-1 ps-1 pe-2.5 transition-colors hover:bg-muted"
              >
                <span className="grid size-8 place-items-center rounded-full bg-primary/10 text-sm font-semibold text-primary uppercase">
                  {me.username.slice(0, 1)}
                </span>
                <Text weight="medium" className="hidden sm:inline">
                  {me.username}
                </Text>
                <Icon name="chevronDown" size={16} className="text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="flex flex-col gap-0.5">
                <Text weight="medium">{me.username}</Text>
                <Text.Hint size="caption">{me.is_admin ? "Admin" : "Operator"}</Text.Hint>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => navigate("/settings")}>
                <Icon name="settings" size={18} />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>
                <Text.Hint size="caption">Theme</Text.Hint>
              </DropdownMenuLabel>
              <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as ThemeChoice)}>
                <DropdownMenuRadioItem value="system">
                  <Icon name="system" size={18} />
                  System
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="light">
                  <Icon name="sun" size={18} />
                  Light
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="dark">
                  <Icon name="moon" size={18} />
                  Dark
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onSelect={() => logout.mutate()}>
                <Icon name="logout" size={18} />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-[1600px] flex-1 flex-col gap-5 px-6 py-6">
        {!me.has_api_key && (
          <div className="flex flex-wrap items-center gap-3 rounded-xl bg-warning/12 px-(--list-inset) py-3">
            <Icon name="warning" variant="Bold" size={20} className="text-warning" />
            <Text className="flex-1">No seller API key yet. Runs can't start until you add one.</Text>
            <Button size="sm" asChild>
              <Link to="/settings">Add key</Link>
            </Button>
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
